import * as THREE from "three";
import type { LoadedHuman } from "./assetLoader";

/** Local Euler XYZ limits (radians) relative to rest pose captured at pose-mode entry. */
export interface JointLimit {
  min: THREE.Vector3;
  max: THREE.Vector3;
}

const DEG = Math.PI / 180;
const DEFAULT_LIMIT = 45 * DEG;

/** Mixamo / Rigify-ish humanoid defaults. Unknown bones → ±45°. */
function limitsForBoneName(name: string): JointLimit {
  const n = name.toLowerCase().replace(/[^a-z0-9]/g, "");

  // Fingers: tighter
  if (/thumb|index|middle|ring|pinky|finger/.test(n)) {
    return v(-60, 60, -20, 20, -20, 20);
  }
  // Elbow / knee — hinge-dominant (X flex)
  if (/forearm|lowerarm|ellbow|elbow/.test(n)) {
    return v(-150, 10, -20, 20, -20, 20);
  }
  if (/(left|right)?(leg|calf|shin)|lowerleg|knee/.test(n) && !/upleg|upperleg|thigh/.test(n)) {
    return v(-10, 150, -15, 15, -15, 15);
  }
  // Shoulder / upper arm
  if (
    /upperarm|shoulder|clavicle|collar/.test(n) ||
    /(left|right)arm$/.test(n) ||
    /^(left|right)arm/.test(n)
  ) {
    return v(-120, 120, -90, 90, -90, 90);
  }
  // Hip / thigh
  if (/upleg|upperleg|thigh|hip/.test(n)) {
    return v(-90, 60, -50, 50, -40, 40);
  }
  // Spine / torso
  if (/spine|chest|torso|abdomen/.test(n)) {
    return v(-40, 40, -35, 35, -35, 35);
  }
  if (/neck/.test(n)) {
    return v(-45, 45, -50, 50, -40, 40);
  }
  if (/head/.test(n)) {
    return v(-50, 50, -70, 70, -45, 45);
  }
  // Wrist / ankle
  if (/hand|wrist/.test(n)) {
    return v(-60, 60, -40, 40, -50, 50);
  }
  if (/foot|ankle|toe/.test(n)) {
    return v(-40, 40, -30, 30, -30, 30);
  }

  return v(
    -DEFAULT_LIMIT / DEG,
    DEFAULT_LIMIT / DEG,
    -DEFAULT_LIMIT / DEG,
    DEFAULT_LIMIT / DEG,
    -DEFAULT_LIMIT / DEG,
    DEFAULT_LIMIT / DEG,
  );
}

function v(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minZ: number,
  maxZ: number,
): JointLimit {
  return {
    min: new THREE.Vector3(minX * DEG, minY * DEG, minZ * DEG),
    max: new THREE.Vector3(maxX * DEG, maxY * DEG, maxZ * DEG),
  };
}

interface BoneEntry {
  bone: THREE.Bone;
  pick: THREE.Mesh;
  rest: THREE.Quaternion;
  limits: JointLimit;
  humanId: string;
}

/**
 * H1: pointer drag on bone pick spheres → local rotation with joint limits.
 * Active only while drive mode is pose (see PoseDriver / setDriveMode).
 */
export class PoseInteract {
  private readonly canvas: HTMLCanvasElement;
  private readonly camera: THREE.Camera;
  private readonly pickRoot: THREE.Group;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly tmpEuler = new THREE.Euler();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly camRight = new THREE.Vector3();
  private readonly camUp = new THREE.Vector3();

  private entries: BoneEntry[] = [];
  private pickToEntry = new Map<THREE.Object3D, BoneEntry>();
  private enabled = false;
  private dragging = false;
  private active: BoneEntry | null = null;
  private lastClientX = 0;
  private lastClientY = 0;
  private disposed = false;
  /** When set, pose picks only that human (H3 multi-human focus). */
  private focusHumanId: string | null = null;

  private readonly pickGeo: THREE.SphereGeometry;
  private readonly pickMat: THREE.MeshBasicMaterial;
  private readonly pickMatActive: THREE.MeshBasicMaterial;

  private readonly onPointerDown: (e: PointerEvent) => void;
  private readonly onPointerMove: (e: PointerEvent) => void;
  private readonly onPointerUp: (e: PointerEvent) => void;

  constructor(canvas: HTMLCanvasElement, camera: THREE.Camera, scene: THREE.Scene) {
    this.canvas = canvas;
    this.camera = camera;
    this.pickRoot = new THREE.Group();
    this.pickRoot.name = "posePickRoot";
    this.pickRoot.visible = false;
    scene.add(this.pickRoot);

    this.pickGeo = new THREE.SphereGeometry(0.045, 12, 12);
    this.pickMat = new THREE.MeshBasicMaterial({
      color: 0x4cc9f0,
      transparent: true,
      opacity: 0.45,
      depthTest: false,
    });
    this.pickMatActive = new THREE.MeshBasicMaterial({
      color: 0xffd166,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });

    this.onPointerDown = (e) => this.handlePointerDown(e);
    this.onPointerMove = (e) => this.handlePointerMove(e);
    this.onPointerUp = (e) => this.handlePointerUp(e);
  }

  setEnabled(enabled: boolean): void {
    if (this.disposed) return;
    this.enabled = enabled;
    this.pickRoot.visible = enabled;
    if (!enabled) {
      this.endDrag();
      this.setActive(null);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** True while a bone marker is being dragged (OrbitControls should yield). */
  isDragging(): boolean {
    return this.dragging;
  }

  /** Focus pose interaction on one human (null = first / all when single). */
  setFocusHumanId(humanId: string | null): void {
    this.focusHumanId = humanId;
    this.endDrag();
    this.setActive(null);
    this.applyFocusVisibility();
  }

  getFocusHumanId(): string | null {
    return this.focusHumanId;
  }

  /** Rebuild pick spheres from skinned skeletons after load. */
  setHumans(humans: LoadedHuman[]): void {
    this.clearEntries();
    if (humans.length > 0) {
      const ids = new Set(humans.map((h) => h.def.id));
      if (!this.focusHumanId || !ids.has(this.focusHumanId)) {
        this.focusHumanId = humans[0].def.id;
      }
    } else {
      this.focusHumanId = null;
    }

    for (const human of humans) {
      const bones = new Set<THREE.Bone>();
      human.root.traverse((obj) => {
        const skinned = obj as THREE.SkinnedMesh;
        if (skinned.isSkinnedMesh && skinned.skeleton) {
          for (const b of skinned.skeleton.bones) {
            bones.add(b);
          }
        }
        if ((obj as THREE.Bone).isBone) {
          bones.add(obj as THREE.Bone);
        }
      });

      for (const bone of bones) {
        const pick = new THREE.Mesh(this.pickGeo, this.pickMat);
        pick.name = `pick:${human.def.id}:${bone.name || "bone"}`;
        pick.userData.boneName = bone.name;
        pick.userData.humanId = human.def.id;
        pick.renderOrder = 10;
        this.pickRoot.add(pick);

        const entry: BoneEntry = {
          bone,
          pick,
          rest: bone.quaternion.clone(),
          limits: limitsForBoneName(bone.name || ""),
          humanId: human.def.id,
        };
        this.entries.push(entry);
        this.pickToEntry.set(pick, entry);
      }
    }

    this.applyFocusVisibility();
    this.syncPickers();
  }

  private applyFocusVisibility(): void {
    const focus = this.focusHumanId;
    for (const entry of this.entries) {
      const on = !focus || entry.humanId === focus;
      // Multi-human: only focused human picks are visible/pickable.
      // Three r170 still raycasts invisible meshes — null raycast to disable.
      entry.pick.visible = on;
      entry.pick.raycast = on ? THREE.Mesh.prototype.raycast : () => {};
    }
  }

  /** Capture current local rotations as joint-limit rest (call when entering pose mode). */
  captureRestFromCurrent(): void {
    for (const entry of this.entries) {
      entry.rest.copy(entry.bone.quaternion);
    }
  }

  /** Follow bones each frame while pose mode is on. */
  syncPickers(): void {
    if (!this.enabled && this.entries.length === 0) return;
    for (const entry of this.entries) {
      entry.bone.getWorldPosition(entry.pick.position);
      // Pick spheres live under pickRoot (identity under scene) → convert world→local.
      this.pickRoot.worldToLocal(entry.pick.position);
    }
  }

  attachListeners(): void {
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
  }

  detachListeners(): void {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.detachListeners();
    this.endDrag();
    this.clearEntries();
    this.pickRoot.parent?.remove(this.pickRoot);
    this.pickGeo.dispose();
    this.pickMat.dispose();
    this.pickMatActive.dispose();
  }

  private clearEntries(): void {
    for (const entry of this.entries) {
      this.pickRoot.remove(entry.pick);
    }
    this.entries = [];
    this.pickToEntry.clear();
    this.active = null;
  }

  private setActive(entry: BoneEntry | null): void {
    if (this.active) {
      this.active.pick.material = this.pickMat;
    }
    this.active = entry;
    if (entry) {
      entry.pick.material = this.pickMatActive;
    }
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.enabled || this.disposed || e.button !== 0) return;

    const hit = this.pickAt(e.clientX, e.clientY);
    if (!hit) return;

    e.preventDefault();
    e.stopPropagation();
    this.canvas.setPointerCapture?.(e.pointerId);
    this.dragging = true;
    this.setActive(hit);
    this.lastClientX = e.clientX;
    this.lastClientY = e.clientY;
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.enabled || !this.dragging || !this.active) return;

    const w = Math.max(1, this.canvas.clientWidth);
    const h = Math.max(1, this.canvas.clientHeight);
    const dx = ((e.clientX - this.lastClientX) / w) * Math.PI * 2;
    const dy = ((e.clientY - this.lastClientY) / h) * Math.PI * 2;
    this.lastClientX = e.clientX;
    this.lastClientY = e.clientY;

    // Screen-space drag → world-axis rotation on the bone, then clamp to limits.
    this.camRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    this.camUp.set(0, 1, 0).applyQuaternion(this.camera.quaternion);

    const bone = this.active.bone;
    bone.rotateOnWorldAxis(this.camUp, dx);
    bone.rotateOnWorldAxis(this.camRight, dy);
    this.clampToLimits(this.active);
    bone.updateMatrix();
    this.syncPickers();
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.dragging) return;
    try {
      this.canvas.releasePointerCapture?.(e.pointerId);
    } catch {
      // ignore if not capturing
    }
    this.endDrag();
  }

  private endDrag(): void {
    this.dragging = false;
  }

  private pickAt(clientX: number, clientY: number): BoneEntry | null {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.camera);

    // Prefer focused-only targets (r170 can hit invisible meshes).
    const focus = this.focusHumanId;
    const targets = focus
      ? this.entries.filter((e) => e.humanId === focus).map((e) => e.pick)
      : this.pickRoot.children;
    const hits = this.raycaster.intersectObjects(targets, false);
    for (const hit of hits) {
      const entry = this.pickToEntry.get(hit.object);
      if (!entry) continue;
      if (focus && entry.humanId !== focus) continue;
      return entry;
    }
    return null;
  }

  /** Clamp bone local quaternion to euler limits relative to rest. */
  private clampToLimits(entry: BoneEntry): void {
    // relative = rest^-1 * current
    this.tmpQuat.copy(entry.rest).invert().multiply(entry.bone.quaternion);
    this.tmpEuler.setFromQuaternion(this.tmpQuat, "XYZ");

    const { min, max } = entry.limits;
    this.tmpEuler.x = THREE.MathUtils.clamp(this.tmpEuler.x, min.x, max.x);
    this.tmpEuler.y = THREE.MathUtils.clamp(this.tmpEuler.y, min.y, max.y);
    this.tmpEuler.z = THREE.MathUtils.clamp(this.tmpEuler.z, min.z, max.z);

    this.tmpQuat.setFromEuler(this.tmpEuler);
    entry.bone.quaternion.copy(entry.rest).multiply(this.tmpQuat);
  }
}
